import React, { useCallback, useState, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import classnames from 'classnames';

// Context
import ExpandNavbarContext from 'Shared/contexts/ExpandNavbarContext';

// Constants
import { billingStates } from 'Shared/constants/constants';
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

// Hooks
import { useQueryParam } from 'Shared/utils/hooks';
import { useApp } from 'Shared/utils/hooks/use-app';

// Components
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import { WpPopover, WpPopoverToggle, WpPopoverContent } from 'Wpds/components/popover';
import WpBackdrop from 'Wpds/components/backdrop/WpBackdrop';
import { WpIllustration, Illustrations } from 'Wpds/components/illustration';

// Styles
import './BillingPlanTimerPopover.scss';

type Props = {
  wrapperComponent?: Function;
};

const defaultImage = Illustrations.two_people_at_table_in_conference_call_purple_bg;
const trialExpiredImage = Illustrations.person_with_megaphone_with_purple_bg;

const BillingPlanTimerPopover = ({ wrapperComponent }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { simpleInviteModal } = useFlags();

  const { intercomContactSalesEventName } = useApp();
  const isExpanded = useContext(ExpandNavbarContext);

  const param = simpleInviteModal
    ? QueryParamConstants.MULTIPLE_INVITE_MODAL
    : QueryParamConstants.INVITE_BY_EMAIL_MODAL;
  const [, setModalParam] = useQueryParam(param);

  const daysLeftInTrial = useSelector(state => state?.organization?.daysLeftInTrial);
  const billingVersion = useSelector(state => state?.organization?.billingVersion);
  const billingState = useSelector(state => state?.organization?.billingState);

  const handleInviteTeammatesClick = useCallback(() => {
    setIsOpen(false);
    setModalParam(true);
  }, [setModalParam]);

  const baseClassName = 'BillingPlanTimerPopover';
  const getProperties = () => {
    if (billingState === billingStates.FREE_TRIAL && typeof daysLeftInTrial === 'number' && daysLeftInTrial >= 0) {
      return {
        btnText: `${daysLeftInTrial} day${daysLeftInTrial > 0 && 's'} left`,
        title: `${daysLeftInTrial} Day${daysLeftInTrial > 0 && 's'} Left of Your Free Trial`,
        img: defaultImage,
        text: (
          <>
            <p>
              You’ve got {`${daysLeftInTrial} day${daysLeftInTrial > 0 && 's'}`} left of your free trial.&nbsp;Invite
              coworkers to get the most out of your trial.
            </p>
          </>
        ),
        buttons: (
          <>
            <WpButtonGroup stacked spacing="lg">
              <WpButton block onClick={handleInviteTeammatesClick} size="lg">
                Invite Teammates
              </WpButton>
              <WpButton
                variant="secondary"
                block
                dataAttributes={{ 'data-intercom': intercomContactSalesEventName }}
                size="lg"
              >
                Contact Sales
              </WpButton>
            </WpButtonGroup>
          </>
        ),
      };
    } else if (billingState === billingStates.TRIAL_EXPIRED) {
      return {
        btnText: 'Trial Expired',
        title: `Sign up for a Growth plan!`,
        img: trialExpiredImage,
        text: (
          <p className={`${baseClassName}__popover__body__text`}>
            To continue using PI Perform, please reach out to sales to upgrade your account or extend your free trial.
          </p>
        ),
        buttons: (
          <WpButton
            dataAttributes={{ 'data-intercom': intercomContactSalesEventName }}
            onClick={() => setIsOpen(true)}
            block
            size="lg"
          >
            Contact Sales
          </WpButton>
        ),
      };
    } else if (billingState === billingStates.FREE_PLAN) {
      return {
        btnText: 'Upgrade',
        title: `Get PI Perform for the Whole Team`,
        img: defaultImage,
        text: (
          <p className={`${baseClassName}__popover__body__text`}>
            You are currently on the Free Plan which is limited to 4 users. Get the most out of PI Perform by upgrading
            today.
          </p>
        ),
        buttons: (
          <WpButton dataAttributes={{ 'data-intercom': intercomContactSalesEventName }} block size="lg">
            Contact Sales
          </WpButton>
        ),
      };
    }
    return {
      btnText: 'Trial Expired',
      title: `Your free trial has expired`,
      img: trialExpiredImage,
      text: (
        <p className={`${baseClassName}__popover__body__text`}>
          To continue using PI Perform, please reach out to sales to upgrade your account or extend your free trial.
        </p>
      ),
      buttons: (
        <WpButton dataAttributes={{ 'data-intercom': intercomContactSalesEventName }} block size="lg">
          Contact Sales
        </WpButton>
      ),
    };
  };

  const classes = classnames(
    baseClassName,
    billingState === billingStates.TRIAL_EXPIRED && `${baseClassName}--expired`
  );

  if (
    billingState === billingStates.PAID_PLAN ||
    billingState === billingStates.PAID_PLAN_PAYMENT_FAILED ||
    billingState === billingStates.PAID_PLAN_PREDICTIVEINDEX ||
    billingVersion !== 2 ||
    !isExpanded
  ) {
    return null;
  }

  const { btnText, title, img, text, buttons } = getProperties();

  const content = (
    <div className={classes}>
      <WpPopover open={isOpen} onOpenChange={setIsOpen}>
        <WpPopoverToggle>
          <WpButton variant="tertiary" size="sm">
            <WpIllustration
              src={Illustrations.pi_logo_circle_red}
              className={`${baseClassName}__logo`}
              alt="Predictive Index logo"
            />
            <span className={`${baseClassName}__btn-text`}>{btnText}</span>
          </WpButton>
        </WpPopoverToggle>
        <WpPopoverContent>
          <div className={`${baseClassName}__popover`}>
            <p className={`${baseClassName}__popover__title`}>{title}</p>
            <div className={`${baseClassName}__popover__body`}>
              <div className={`${baseClassName}__image`}>
                <img className={`${baseClassName}__image__img`} src={img} alt="Trial" />
              </div>
              {text && text}
              {buttons && buttons}
            </div>
          </div>
        </WpPopoverContent>
      </WpPopover>
    </div>
  );

  const Wrapper = wrapperComponent ? wrapperComponent : null;

  return (
    <>
      {wrapperComponent ? <Wrapper>{content}</Wrapper> : <>{content}</>}
      {isOpen && <WpBackdrop onClick={() => setIsOpen(false)} transparent />}
    </>
  );
};

export default BillingPlanTimerPopover;
